import { IMessagingClient } from '@agoda/messaging-client';
import { ADPTags } from '@services/types';
import { AppContextData } from '@contexts/app/AppContextData';
import {
    Messenger,
    PasskeyEventMessage,
    PasskeyEventName,
    PasskeyOperation,
    PasskeyErrorCategory,
    UalCaptchaAnalyticMessage,
} from '@services/hadoopMessage/Messenger';
import { Tags } from '@services/measurement';

const applicationName = 'universal-login';
const ualSocialLoginConversion = 'UalSocialLoginConversion';
const passkeyEventTableName = 'passkeyevent';

export class HadoopMessenger implements Messenger {
    private readonly messagingClient: IMessagingClient;
    private readonly appContext: AppContextData;
    private readonly baseTags: ADPTags = {};

    constructor(messagingClient: IMessagingClient, appContext: AppContextData) {
        this.messagingClient = messagingClient;
        this.appContext = appContext;

        if (typeof (this.messagingClient as any).context === 'function') {
            (this.messagingClient as any).context({
                sessionId: this.appContext.sessionId,
                userId: this.appContext.userId,
            });
        }

        const { appId, origin, userId, sessionId, correlationId, clientVersion } = appContext;
        const whitelabelId = `${appContext.whitelabelId}`;
        this.baseTags = {
            app_id: appId,
            origin: origin,
            user_id: userId,
            session_id: sessionId,
            white_label_id: whitelabelId,
            correlation_id: correlationId,
            client_version: clientVersion,
        };
    }

    public send(messagingTable: string, error?: Error, tags?: ADPTags) {
        this.sender(messagingTable, error, tags);
    }

    private sender(messagingTable: string, error?: Error, tags?: ADPTags) {
        const ualCaptchaAnalyticMessage: UalCaptchaAnalyticMessage = {
            application_name: applicationName,
            app_id: this.baseTags.app_id,
            origin: this.baseTags.origin,
            session_id: this.baseTags.session_id,
            user_id: this.baseTags.user_id,
            white_label_id: parseInt(this.baseTags.white_label_id),
            correlation_id: this.baseTags.correlation_id,
            captcha_session_id: !!tags && tags.captcha_session_id ? tags.captcha_session_id : 'unknown',
            captcha_type: !!tags && tags.captcha_type ? tags.captcha_type : 'unknown',
            captcha_id: !!tags && tags.captcha_id ? tags.captcha_id : 'unknown',
            captcha_event: !!tags && tags.captcha_event ? tags.captcha_event : 'unknown',
            duration: !!tags && tags.duration ? parseInt(tags.duration) : 0,
            error_message: error?.message ?? undefined,
        };

        const msg = this.messagingClient.create(messagingTable as any, ualCaptchaAnalyticMessage as any);
        this.messagingClient.send(msg);
    }

    public sendPasskeyEvent(message: Partial<PasskeyEventMessage>): void {
        const completeMessage: PasskeyEventMessage = {
            user_id: message.user_id ?? this.baseTags.user_id,
            application_type: message.application_type ?? this.baseTags.app_id,
            member_id: message.member_id ?? null,
            app_version: message.app_version ?? this.baseTags.client_version ?? null,
            os_version: message.os_version ?? navigator.userAgent,
            operation: message.operation ?? PasskeyOperation.UNKNOWN,
            event_name: message.event_name ?? PasskeyEventName.UNKNOWN,
            response_code: message.response_code ?? null,
            note: message.note ?? null,
            trigger_source: message.trigger_source ?? null,
            metadata: message.metadata ?? null,
            error_code: message.error_code ?? null,
            error_description: message.error_description ?? null,
            error_category: message.error_category ?? null,
            timestamp: message.timestamp ?? Date.now(),
        };
        const msg = this.messagingClient.create(passkeyEventTableName as any, completeMessage as any);
        this.messagingClient.send(msg);
    }

    /**
     * Track impression of social login buttons to measure how many users see the option
     * This is the first step in the conversion funnel
     */
    public trackSocialLoginImpression(userId: string): void {
        this.send(ualSocialLoginConversion, undefined, {
            event_type: 'impression',
            user_id: userId,
            login_method: 'social',
            timestamp: Date.now().toString(),
        });
    }

    /**
     * Track when a user attempts to log in with a social provider
     * This is the second step in the conversion funnel
     */
    public trackSocialLoginAttempt(userId: string, socialType: string): void {
        this.send(ualSocialLoginConversion, undefined, {
            event_type: 'attempt',
            user_id: userId,
            login_method: socialType,
            timestamp: Date.now().toString(),
        });
    }

    /**
     * Track when a social login attempt completes (successfully or with error)
     * This is the final step in the conversion funnel
     */
    public trackSocialLoginConversion(userId: string, socialType: string, success: boolean, error?: string): void {
        const tags: ADPTags = {
            event_type: 'conversion',
            user_id: userId,
            login_method: socialType,
            success: success ? 'true' : 'false',
            timestamp: Date.now().toString(),
        };

        if (error) {
            tags.error = error;
        }

        this.send(ualSocialLoginConversion, undefined, tags);
    }
}
